import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button, Grid, Typography, Paper, Box, Card, CardContent, IconButton, Chip, Avatar, LinearProgress, Divider, Fade, Grow } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  Storefront,
  Event,
  AccountBalance,
  HealthAndSafety,
  ArrowForward,
  TrendingUp,
  Timeline,
  Insights,
  ShoppingBag,
  CalendarMonth,
  StickyNote2,
  AccountBalanceWallet,
  LocalOffer,
  Schedule,
  Assignment,
  FitnessCenter,
  AttachMoney,
  CalendarToday,
  BusinessCenter,
  Analytics
} from "@mui/icons-material";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import axiosInstance from "../../utils/axios/axiosInstance";
import { userApiLoader } from "../../action/loaderActions";
import { toast } from "react-toastify";
import bgImage from "../../assets/images/bg.jpg";

// Clean business color palette
const BUSINESS_COLORS = {
  primary: '#c942b1',      // Professional blue
  secondary: '#424242',    // Neutral gray
  accent: '#00695C',       // Teal accent
  success: '#2E7D0A',      // Business green
  warning: '#F57C00',      // Professional orange
  error: '#C62828',        // Business red
  text: {
    primary: '#212121',
    secondary: '#000000',
    disabled: '#9E9E9E'
  },
  divider: '#E0E0E0',
  surface: '#FFFFFF',
  background: '#FAFAFA'
};

const CHART_COLORS = ['#1565C0', '#424242', '#00695C', '#2E7D0A', '#F57C00', '#C62828'];

const CategoryCard = ({ title, icon, color, description, path, data, onViewMore }) => {
  const navigate = useNavigate();

  const renderHealthOverview = () => {
    if (!data || data.length === 0) {
      return (
        <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Box 
            sx={{
              width: 80,
              height: 80,
              border: `3px solid ${BUSINESS_COLORS.divider}`,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              backgroundColor: BUSINESS_COLORS.surface
            }}
          >
            <FitnessCenter sx={{ color: BUSINESS_COLORS.text.secondary, fontSize: 32 }} />
          </Box>
          <Typography variant="h6" sx={{ color: BUSINESS_COLORS.text.primary, fontWeight: 600, mb: 1, textAlign: 'center' }}>
            No Health Data
          </Typography>
          <Typography variant="body2" sx={{ color: BUSINESS_COLORS.text.secondary, textAlign: 'center' }}>
            Start tracking your health metrics
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <ResponsiveContainer width="100%" height={140}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={30}
                outerRadius={55}
                paddingAngle={2}
                dataKey="value"
              >
                {data.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={CHART_COLORS[index % CHART_COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{
                  backgroundColor: BUSINESS_COLORS.surface,
                  border: `1px solid ${BUSINESS_COLORS.divider}`,
                  borderRadius: '8px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  fontSize: '12px'
                }}
              />
              <Legend 
                wrapperStyle={{ fontSize: '10px', paddingTop: '10px' }}
                iconSize={8}
              />
            </PieChart>
          </ResponsiveContainer>
        </Box>

        <Grid container spacing={1.5} sx={{ mt: 1 }}>
          {data.slice(0, 2).map((metric, index) => (
            <Grid item xs={6} key={index}>
              <Box
                sx={{
                  p: 2,
                  border: `1px solid ${BUSINESS_COLORS.divider}`,
                  borderRadius: '8px',
                  textAlign: 'center',
                  backgroundColor: BUSINESS_COLORS.surface
                }}
              >
                <Typography variant="body2" sx={{ color: BUSINESS_COLORS.text.secondary, fontSize: '11px', display: 'block', mb: 0.5 }}>
                  {metric.name}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: CHART_COLORS[index], fontSize: '16px' }}>
                  {metric.value}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  const renderMarketplaceProducts = () => {
    if (!data || data.length === 0) {
      return (
        <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Box 
            sx={{
              width: 80,
              height: 80,
              border: `3px solid ${BUSINESS_COLORS.divider}`,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              backgroundColor: BUSINESS_COLORS.surface
            }}
          >
            <ShoppingBag sx={{ color: BUSINESS_COLORS.text.secondary, fontSize: 32 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: BUSINESS_COLORS.text.primary, mb: 1, textAlign: 'center' }}>
            No Products Available
          </Typography>
          <Typography variant="body2" sx={{ color: BUSINESS_COLORS.text.secondary, textAlign: 'center' }}>
            No marketplace listings found
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 3, height: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {data.slice(0, 3).map((item, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 2.5,
                border: `1px solid ${BUSINESS_COLORS.divider}`,
                borderRadius: '8px',
                backgroundColor: BUSINESS_COLORS.surface,
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: color,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  transform: 'translateY(-1px)'
                }
              }}
            >
              <Avatar
                src={item.thumbnail}
                sx={{ 
                  width: 48, 
                  height: 48, 
                  borderRadius: '8px',
                  mr: 2.5,
                  border: `1px solid ${BUSINESS_COLORS.divider}`
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    fontWeight: 700,
                    color: BUSINESS_COLORS.text.primary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    mb: 0.5,
                    fontSize: '14px'
                  }}
                >
                  {item.name}
                </Typography>
                <Typography variant="body2" sx={{ color: BUSINESS_COLORS.text.secondary, fontSize: '12px' }}>
                  Product ID: #{item.id}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const renderEvents = () => {
    if (!data || data.length === 0) {
      return (
        <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Box 
            sx={{
              width: 80,
              height: 80,
              border: `3px solid ${BUSINESS_COLORS.divider}`,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              backgroundColor: BUSINESS_COLORS.surface
            }}
          >
            <CalendarMonth sx={{ color: BUSINESS_COLORS.text.secondary, fontSize: 32 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: BUSINESS_COLORS.text.primary, mb: 1, textAlign: 'center' }}>
            No Events Scheduled
          </Typography>
          <Typography variant="body2" sx={{ color: BUSINESS_COLORS.text.secondary, textAlign: 'center' }}>
            No upcoming events found
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 3, height: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {data.slice(0, 3).map((item, index) => (
            <Box
              key={index}
              sx={{
                p: 2.5,
                border: `1px solid ${BUSINESS_COLORS.divider}`,
                borderRadius: '8px',
                backgroundColor: BUSINESS_COLORS.surface,
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: color,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  transform: 'translateY(-1px)'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                <Avatar
                  src={item.image}
                  sx={{ 
                    width: 48, 
                    height: 48, 
                    borderRadius: '8px',
                    border: `1px solid ${BUSINESS_COLORS.divider}`
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 700,
                      color: BUSINESS_COLORS.text.primary,
                      mb: 0.5,
                      lineHeight: 1.3,
                      fontSize: '14px'
                    }}
                  >
                    {item.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: BUSINESS_COLORS.text.secondary, fontSize: '12px' }}>
                    Event ID: #{item.id}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const renderStickyNotes = () => {
    if (!data || data.length === 0) {
      return (
        <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Box 
            sx={{
              width: 80,
              height: 80,
              border: `3px solid ${BUSINESS_COLORS.divider}`,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              backgroundColor: BUSINESS_COLORS.surface
            }}
          >
            <StickyNote2 sx={{ color: BUSINESS_COLORS.text.secondary, fontSize: 32 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: BUSINESS_COLORS.text.primary, mb: 1, textAlign: 'center' }}>
            No Notes Available
          </Typography>
          <Typography variant="body2" sx={{ color: BUSINESS_COLORS.text.secondary, textAlign: 'center' }}>
            Create your first sticky note
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 3, height: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {data.slice(0, 3).map((item, index) => (
            <Box
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                navigate(path);
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 2.5,
                border: `1px solid ${BUSINESS_COLORS.divider}`,
                borderRadius: '8px',
                backgroundColor: BUSINESS_COLORS.surface,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: color,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  transform: 'translateY(-1px)'
                }
              }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: color,
                  mr: 2.5,
                  flexShrink: 0
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    fontWeight: 700,
                    color: BUSINESS_COLORS.text.primary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    mb: 0.5,
                    fontSize: '14px'
                  }}
                >
                  {item.name}
                </Typography>
                <Typography variant="body2" sx={{ color: BUSINESS_COLORS.text.secondary, fontSize: '12px' }}>
                  Note ID: #{item.id}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const renderFinancialOverview = () => {
    if (!data) {
      return (
        <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Box 
            sx={{
              width: 80,
              height: 80,
              border: `3px solid ${BUSINESS_COLORS.divider}`,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              backgroundColor: BUSINESS_COLORS.surface
            }}
          >
            <AccountBalanceWallet sx={{ color: BUSINESS_COLORS.text.secondary, fontSize: 32 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: BUSINESS_COLORS.text.primary, mb: 1, textAlign: 'center' }}>
            No Budget Data
          </Typography>
          <Typography variant="body2" sx={{ color: BUSINESS_COLORS.text.secondary, textAlign: 'center' }}>
            Set up your budget tracker
          </Typography>
        </Box>
      );
    }

    const { weekly, monthly, yearly } = data;
    const activeBudget = monthly || yearly || weekly;

    if (!activeBudget) {
      return (
        <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Box 
            sx={{
              width: 80,
              height: 80,
              border: `3px solid ${BUSINESS_COLORS.divider}`,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              backgroundColor: BUSINESS_COLORS.surface
            }}
          >
            <AccountBalanceWallet sx={{ color: BUSINESS_COLORS.text.secondary, fontSize: 32 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: BUSINESS_COLORS.text.primary, mb: 1, textAlign: 'center' }}>
            No Active Budget
          </Typography>
          <Typography variant="body2" sx={{ color: BUSINESS_COLORS.text.secondary, textAlign: 'center' }}>
            Create your budget plan
          </Typography>
        </Box>
      );
    }

    const categories = JSON.parse(activeBudget.categories || '[]');
    const budgetType = monthly ? 'Monthly' : yearly ? 'Yearly' : 'Weekly';

    return (
      <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: `1px solid ${BUSINESS_COLORS.divider}`,
            borderRadius: '12px',
            mb: 3,
            textAlign: 'center',
            backgroundColor: BUSINESS_COLORS.surface
          }}
        >
          <Typography variant="caption" sx={{ color: BUSINESS_COLORS.text.secondary, fontSize: '12px', fontWeight: 700, display: 'block', mb: 1 }}>
            {budgetType} Budget
          </Typography>
          <Typography 
            variant="h4" 
            sx={{ 
              color: BUSINESS_COLORS.primary,
              fontWeight: 700,
              mb: 1
            }}
          >
            ${activeBudget.total_budget}
          </Typography>
          <Typography variant="caption" sx={{ color: BUSINESS_COLORS.text.secondary, fontSize: '11px' }}>
            {activeBudget.budget_month_year || 'Current Period'}
          </Typography>
        </Paper>
        
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: BUSINESS_COLORS.text.primary }}>
            Budget Categories
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {categories.slice(0, 3).map((cat, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 2,
                  border: `1px solid ${BUSINESS_COLORS.divider}`,
                  borderRadius: '8px',
                  backgroundColor: BUSINESS_COLORS.surface
                }}
              >
                <Typography variant="subtitle1" sx={{ fontSize: '14px', color: BUSINESS_COLORS.text.primary, fontWeight: 700 }}>
                  {cat.category}
                </Typography>
                <Typography variant="subtitle1" sx={{ color: BUSINESS_COLORS.success, fontWeight: 700, fontSize: '14px' }}>
                  ${cat.limit}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  };

  const renderMyAppointments = () => {
    if (!data || data.length === 0) {
      return (
        <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Box 
            sx={{
              width: 80,
              height: 80,
              border: `3px solid ${BUSINESS_COLORS.divider}`,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              backgroundColor: BUSINESS_COLORS.surface
            }}
          >
            <CalendarToday sx={{ color: BUSINESS_COLORS.text.secondary, fontSize: 32 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: BUSINESS_COLORS.text.primary, mb: 1, textAlign: 'center' }}>
            No Appointments
          </Typography>
          <Typography variant="body2" sx={{ color: BUSINESS_COLORS.text.secondary, textAlign: 'center' }}>
            Schedule your appointments
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 3, height: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {data.slice(0, 3).map((item, index) => (
            <Box
              key={index}
              sx={{
                p: 2.5,
                border: `1px solid ${BUSINESS_COLORS.divider}`,
                borderRadius: '8px',
                backgroundColor: BUSINESS_COLORS.surface,
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: color,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  transform: 'translateY(-1px)'
                }
              }}
            >
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontWeight: 700,
                  color: BUSINESS_COLORS.text.primary,
                  mb: 1,
                  fontSize: '14px'
                }}
              >
                {item.title || item.name || 'Appointment'}
              </Typography>
              <Typography variant="body2" sx={{ color: BUSINESS_COLORS.text.secondary, fontSize: '12px' }}>
                {item.date || item.time || 'Time TBD'}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const renderContent = () => {
    switch(title) {
      case 'Marketplace Products': return renderMarketplaceProducts();
      case 'Events': return renderEvents();
      case 'Sticky Notes': return renderStickyNotes();
      case 'Financial Overview': return renderFinancialOverview();
      case 'Health Overview': return renderHealthOverview();
      case 'My Appointments': return renderMyAppointments();
      default: return null;
    }
  };

  return (
    <Card
      sx={{
        height: 480,
        backgroundColor: BUSINESS_COLORS.surface,
        border: `1px solid ${BUSINESS_COLORS.divider}`,
        borderRadius: '12px',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
          borderColor: color,
        }
      }}
      onClick={() => navigate(path)}
    >
      <CardContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ p: 3, pb: 1, borderBottom: `1px solid ${BUSINESS_COLORS.divider}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  border: `2px solid ${color}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: color,
                  flexShrink: 0,
                  backgroundColor: BUSINESS_COLORS.surface
                }}
              >
                {React.cloneElement(icon, { fontSize: 'medium' })}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700,
                    color: BUSINESS_COLORS.text.primary,
                    mb: 0.5,
                    fontSize: '16px'
                  }}
                >
                  {title}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: BUSINESS_COLORS.text.secondary,
                    lineHeight: 1.4,
                    fontSize: '13px'
                  }}
                >
                  {description}
                </Typography>
              </Box>
            </Box>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onViewMore && onViewMore();
              }}
              sx={{
                width: 36,
                height: 36,
                border: `1px solid ${BUSINESS_COLORS.divider}`,
                backgroundColor: BUSINESS_COLORS.surface,
                color: color,
                flexShrink: 0,
                '&:hover': {
                  backgroundColor: `${color}08`,
                  borderColor: color,
                  transform: 'scale(1.1)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <ArrowForward sx={{ fontSize: '16px' }} />
            </IconButton>
          </Box>
        </Box>
        
        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          {renderContent()}
        </Box>
      </CardContent>
    </Card>
  );
};

const Home = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { UserEmail } = useSelector((state) => state.user);
  
  const [dashboardData, setDashboardData] = useState({
    ads: [],
    events: [],
    notes: [],
    budgets: null,
    health_data: [],
    appointments: []
  });

  const [loading, setLoading] = useState(true);

  const DASHBOARD_API_URL = `${process.env.REACT_APP_USER_MANAGEMENT_API_BASE_URL}/v1/dashboard`;

  const fetchDashboardData = async () => {
    try {
      dispatch(userApiLoader({ isApiLoader: true }));
      setLoading(true);
      
      const response = await axiosInstance.get(DASHBOARD_API_URL);
      
      if (response.data && response.data.status === 200) {
        const { ads, events, notes, budgets, health_data, appointments } = response.data;
        
        setDashboardData({
          ads: ads || [],
          events: events || [],
          notes: notes || [],
          budgets: budgets || null,
          health_data: health_data || [],
          appointments: appointments || []
        });
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error(
        error?.response?.data?.message || 'Failed to load dashboard data',
        { autoClose: 6000 }
      );
    } finally {
      dispatch(userApiLoader({ isApiLoader: false }));
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const cards = [
    {
      title: "My Appointments",
      icon: <CalendarToday />,
      color: BUSINESS_COLORS.primary,
      description: "Manage your scheduled appointments and meetings.",
      path: "/appointments",
      data: dashboardData.appointments
    },
    {
      title: "Marketplace Products",
      icon: <Storefront />,
      color: BUSINESS_COLORS.primary,
      description: "Browse and manage marketplace product listings.",
      path: "/marketplace",
      data: dashboardData.ads
    },
    {
      title: "Events",
      icon: <Event />,
      color: BUSINESS_COLORS.primary,
      description: "View and participate in campus events and activities.",
      path: "/campusandcommunity/events",
      data: dashboardData.events
    },
    {
      title: "Health Overview",
      icon: <HealthAndSafety />,
      color: BUSINESS_COLORS.primary,
      description: "Track and monitor your health and fitness metrics.",
      path: "/healthandfitness/dashboard",
      data: dashboardData.health_data
    },
    {
      title: "Financial Overview",
      icon: <AccountBalance />,
      color: BUSINESS_COLORS.primary,
      description: "Monitor your budget and financial planning tools.",
      path: "/organizefinance/accountoverview",
      data: dashboardData.budgets
    },
    {
      title: "Sticky Notes",
      icon: <StickyNote2 />,
      color: BUSINESS_COLORS.primary,
      description: "Access your personal notes and reminders.",
      path: "/studenttools/notes",
      data: dashboardData.notes
    }
  ];

  const handleViewMore = (card) => {
    navigate(card.path);
  };

  if (loading) {
    return (
      <Box 
        sx={{
          backgroundImage: `url(${require('../../assets/images/bg.jpg')})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          minHeight: 'calc(100vh - 110px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 4,
          py: 10,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(250, 250, 250, 0.7)', // Semi-transparent overlay
            backdropFilter: 'blur(2px)',
            zIndex: 0
          }
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 6,
            borderRadius: '16px',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            border: `1px solid ${BUSINESS_COLORS.divider}`,
            textAlign: 'center',
            maxWidth: 400,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            position: 'relative',
            zIndex: 1,
            backdropFilter: 'blur(10px)'
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                border: `3px solid ${BUSINESS_COLORS.primary}`,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
                backgroundColor: BUSINESS_COLORS.surface
              }}
            >
              <Analytics sx={{ fontSize: 28, color: BUSINESS_COLORS.primary }} />
            </Box>
          </Box>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700, 
              color: BUSINESS_COLORS.text.primary, 
              mb: 2
            }}
          >
            Loading Dashboard
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: BUSINESS_COLORS.text.secondary,
              mb: 3
            }}
          >
            Preparing your business overview...
          </Typography>
          <LinearProgress 
            sx={{ 
              borderRadius: 2,
              height: 4,
              backgroundColor: `${BUSINESS_COLORS.primary}20`,
              '& .MuiLinearProgress-bar': {
                backgroundColor: BUSINESS_COLORS.primary
              }
            }} 
          />
        </Paper>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        backgroundImage: `url(${require('../../assets/images/bg.jpg')})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        minHeight: 'calc(100vh - 110px)',
        px: 4,
        py: 6,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(250, 250, 250, 0.50)', 
          backdropFilter: 'blur(2px)',
          zIndex: 0
        }
      }}
    >
      <Box sx={{ maxWidth: '1200px', mx: 'auto', position: 'relative', zIndex: 1 }}>
        {/* Header Section */}
      

        {/* Cards Grid */}
        <Box sx={{ mb: 6 }}>
          <Grid container spacing={3}>
            {cards.map((card, index) => (
              <Grid item xs={12} sm={6} lg={4} key={index} className="dashboardblog">
                <CategoryCard  
                  {...card} 
                  onViewMore={() => handleViewMore(card)}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default Home;